import { useEffect, useRef, useState } from "react";
import axiosInstance from "services/axiosInstance";
import { COPILOT_AGENT_HEALTH } from "services/CONSTANTS";

const POLL_INTERVAL_MS = 30000;
// ASTT-7496 (Group 4 housekeeping): short grace window before believing a
// single failed health check -- see checkNow's remarks below.
const RETRY_DELAY_MS = 3000;

/**
 * Polls api/v1/copilotagent/health and returns a live status:
 * "checking" | "online" | "offline". Direct copy of useAgentStatus.js's
 * pattern, pointed at the Copilot Studio path's health endpoint instead --
 * see that file for the fuller rationale (ARIA project precedent, why this
 * lives in the Fab rather than the Panel).
 */
export default function useCopilotAgentStatus(enabled) {
  const [status, setStatus] = useState("checking");
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;
    cancelledRef.current = false;

    const pingOnce = async () => {
      try {
        const response = await axiosInstance.get(COPILOT_AGENT_HEALTH());
        const healthy = response?.data?.data?.healthy ?? response?.data?.healthy;
        return !!healthy;
      } catch {
        return false;
      }
    };

    // ASTT-7496 (Group 4 housekeeping): a single dropped health check
    // (transient network blip, not a real outage) previously flipped the
    // whole composer to "offline"/read-only for up to POLL_INTERVAL_MS (30s)
    // -- until the NEXT scheduled poll happened to succeed and correct it.
    // On a failed check, this now waits a short RETRY_DELAY_MS and checks
    // again immediately, ONLY reporting "offline" if that retry also fails.
    // A genuine outage is still detected within a few seconds -- not
    // meaningfully slower than before -- but a one-off blip no longer
    // visibly flickers the composer to disabled and back.
    const checkNow = async () => {
      const firstResult = await pingOnce();
      if (cancelledRef.current) return;

      if (firstResult) {
        setStatus("online");
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      if (cancelledRef.current) return;

      const retryResult = await pingOnce();
      if (cancelledRef.current) return;
      setStatus(retryResult ? "online" : "offline");
    };

    checkNow();
    const interval = setInterval(checkNow, POLL_INTERVAL_MS);

    return () => {
      cancelledRef.current = true;
      clearInterval(interval);
    };
  }, [enabled]);

  return status;
}
